<template>
  <div class="h-full group" @click="expandCard" @mouseover="onMouseOver" @mouseout="onMouseOut">
    <div ref="expandablePanelElement" class="absolute left-0 scroll-anchor" />
    <div
      class="flex flex-col justify-between w-full border-b p-4 transition-colors duration-1000 ease-in-out"
      :class="{
        'bg-neutral-background-secondary border-primary-border-default': isHoveredOver,
        'min-h-[90px] border-neutral-border-default': !expansionPanel.isShown.value,
        'h-fit border-primary-border-default': expansionPanel.isShown.value,
      }"
    >
      <ExpansionPanel
        :model-value="expansionPanel.isShown.value"
        disable-close-animation
        disable-open-animation
        :class="{
          'space-y-2.5': expansionPanel.isShown.value,
        }"
      >
        <template #activator>
          <div class="flex items-start space-x-2.5">
            <FullViewButton
              :review-item="reviewItem"
              :pdf-price="pdfPrice"
              :is-pdf-price-loading="isPdfPriceLoading"
              :is-mira-integration-enabled="isMiraIntegrationEnabled"
              @fetch-pdf-price="getArticlePdfPrice"
            />
            <div
              v-if="attributeType === AttributeStructureType.MedicalAlternatives"
              class="flex flex-wrap gap-1.5 items-center"
            >
              <span
                v-for="(alt, i) in (attributeValue as MedicalAlternativesValue)?.alternatives"
                :key="i"
                class="flex items-center px-2 py-1 border border-blue-800 rounded-lg text-sm font-medium text-blue-800 bg-indigo-50"
              >
                {{ alt.name || `Alternative ${i + 1}` }}
              </span>
            </div>
            <div
              v-else-if="attributeType === AttributeStructureType.StudyDesign"
              class="flex-1 h-36"
            >
              <StudyDesignThumbnail
                :populations="(attributeValue as StudyDesignValue)?.populations ?? []"
                :arms="(attributeValue as StudyDesignValue)?.arms ?? []"
                :periods="(attributeValue as StudyDesignValue)?.periods ?? []"
                :intervention-definitions="
                  (attributeValue as StudyDesignValue)?.interventionDefinitions ?? []
                "
                class="h-full"
              />
            </div>
            <div
              v-else
              class="text-sm text-neutral-text-body"
              :class="{
                'line-clamp-3': !expansionPanel.isShown.value,
              }"
            >
              {{ extractedText }}
            </div>
          </div>
        </template>

        <template #default>
          <div class="w-full">
            <div class="flex items-center justify-between w-full gap-4">
              <slot name="expanded-header">
                <a class="underline flex items-center" :href="itemUrl" target="_blank" @click.stop>
                  <SourceIcon :review-item="reviewItem" />
                </a>
              </slot>

              <div class="text-primary-text-default flex items-center ml-auto">
                #
                <p v-html="formattedId" />
              </div>
            </div>
          </div>
        </template>
      </ExpansionPanel>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, toRef } from "vue";
import ExpansionPanel from "@app/components/Global/ExpansionPanel.vue";
import FullViewButton from "../ReviewItemDetails/components/FullViewButton.vue";
import SourceIcon from "../ReviewItemDetails/components/SourceIcon.vue";
import { ReviewItem } from "@core/domain/models/reviewItem.model";
import { useReviewItemDetails } from "../ReviewItemDetails/use-review-item-details";
import useExpansionPanel from "@app/composables/use-expansion-panel";
import { StudyDesignThumbnail } from "visual-study-design-builder";
import { AttributeStructureType } from "@core/domain/types/attributeStructureType.type";
import type {
  MedicalAlternativesValue,
  StudyDesignValue,
} from "@core/domain/types/attributeValue.type";

const expansionPanel = useExpansionPanel();

const props = withDefaults(
  defineProps<{
    reviewItem: ReviewItem;
    extractedText: string;
    itemUrl?: string;
    attributeValue?: MedicalAlternativesValue | StudyDesignValue | null;
    attributeType?: string;
  }>(),
  {
    itemUrl: "",
    extractedText: "",
    attributeValue: null,
    attributeType: undefined,
  },
);

const expandablePanelElement = ref<HTMLElement>();

const {
  isHoveredOver,
  formattedId,
  pdfPrice,
  isPdfPriceLoading,
  isMiraIntegrationEnabled,
  getArticlePdfPrice,
  expandCard,
  onMouseOver,
  onMouseOut,
} = useReviewItemDetails(toRef(props, "reviewItem"), expansionPanel, expandablePanelElement);
</script>
